"use client";

import React from "react";
import { ArrowRight, CircleCheck, FileText, Database, Quote, Layers3 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GovernedAiAnswersFromApprovedContentOnly() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="governed-ai-answers-from-approved-content-only" className="relative overflow-hidden bg-background pt-14 pb-14 lg:pt-20 lg:pb-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/6 via-background to-background" />
      <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-primary/5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
          <div ref={leftRef} className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 ease-out ${isLeftVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="space-y-6">
              <Badge variant="outline" className="border-border/60 bg-background/70 px-3 py-1.5 text-foreground backdrop-blur-sm">
                <Layers3 className="mr-2 size-4 text-primary" />
                Ready-to-Deploy RAG for Company Knowledge
              </Badge>

              <div className="space-y-5">
                <h1 className="max-w-4xl text-4xl font-bold leading-[0.98] tracking-tight text-foreground md:text-5xl lg:text-[3.7rem]">
                  A ready-to-deploy{" "}
                  <span className="relative whitespace-nowrap text-primary">
                    RAG platform
                    <svg className="absolute -bottom-2 left-0 h-3 w-full text-primary/20" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" />
                    </svg>
                  </span>
                  <br />
                  for businesses and agencies
                </h1>

                <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-[1.15rem]">
                  EloRAG turns approved company knowledge into grounded AI answers with citations,
                  controlled access, and built-in governance — without having to build your own
                  retrieval stack.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button size="lg" className="h-12 px-8 text-base shadow-sm" asChild>
                <Link to="/BookADemoContactSalesPage">
                  Book a Demo <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="h-12 bg-background px-8 text-base" asChild>
                <Link to="/PricingPage">View Pricing</Link>
              </Button>
            </div>

            <div className="flex flex-wrap gap-2.5 pt-3">
              <div className="rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm font-medium text-foreground/90">
                Citation-based answers
              </div>
              <div className="rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm font-medium text-foreground/90">
                Approved content only
              </div>
              <div className="rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm font-medium text-foreground/90">
                White-label ready
              </div>
              <div className="rounded-full border border-border/60 bg-muted/40 px-4 py-2 text-sm font-medium text-foreground/90">
                BYO-Key pricing
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`relative lg:col-span-5 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"}`}>
            <div className="absolute left-1/2 top-1/2 -z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 blur-3xl" />

            <div className="relative mx-auto w-full max-w-md lg:ml-auto lg:mr-0">
              <Card className="relative z-10 overflow-visible border-border/60 bg-background/95 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.18)] backdrop-blur-xl">
                <CardHeader className="rounded-t-xl border-b border-border/40 bg-muted/30 px-6 pt-5 pb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="rounded-md bg-primary/10 p-1.5">
                        <Database className="size-4 text-primary" />
                      </div>
                      <span className="text-sm font-semibold text-foreground">
                        Approved Knowledge Retrieval
                      </span>
                    </div>

                    <Badge className="gap-1.5 border-emerald-500/20 bg-emerald-500/10 py-1 text-emerald-600">
                      <CircleCheck className="size-3.5" />
                      Live
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-5 p-6">
                  <div className="space-y-3">
                    <div className="h-2.5 w-3/4 rounded-full bg-muted" />
                    <div className="h-2.5 w-full rounded-full bg-muted" />
                    <div className="h-2.5 w-5/6 rounded-full bg-muted" />
                  </div>

                  <div className="relative rounded-lg border border-primary/10 bg-primary/5 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Quote className="size-4 text-primary" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                        Answer Preview
                      </span>
                    </div>

                    <p className="text-sm leading-relaxed text-foreground">
                      “The system answers from approved knowledge only, provides source citations,
                      and refuses when evidence is insufficient instead of generating unsupported output.”
                      <span className="ml-1.5 inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground shadow-sm">
                        1
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="absolute -right-1 -bottom-4 z-20 w-60 border-border/60 bg-background shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] md:right-2 md:bottom-0">
                <CardContent className="flex items-start gap-3.5 p-4">
                  <div className="mt-0.5 rounded-lg bg-primary/10 p-2 shrink-0">
                    <FileText className="size-5 text-primary" />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">
                        Citation [1]
                      </span>
                    </div>
                    <p className="mb-1 text-sm font-medium leading-tight text-foreground">
                      Approved Knowledge Policy
                    </p>
                    <p className="line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
                      Only approved content is indexed and retrieved. Unsupported answers must be refused.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}